/**
 * @fileoverview Configuration loader for CasebookMap.
 * Handles loading, parsing, and validating map configuration from JSON files
 * and tilemapresource.xml files (GDAL2Tiles format).
 * @module config/ConfigLoader
 */


import { loadJsonDataFromPath, fixRelativeChildPathWithParentFilePath, stripQueryString, appendCacheBuster, getOwnValue } from '../utils/jrfuncs.js';
import { DEBUG_SIMULATE_NETWORK_DELAY_MS } from '../constants.js';


/**
 * Utility class for loading and validating map configuration.
 * @class
 */
export class ConfigLoader {
    /**
     * Creates a new ConfigLoader instance.
     * @param {Object} mapApp - The MapApp instance
     */
    constructor(mapApp) {
        this.mapApp = mapApp;
    }


    // ==========================================
    // LOGGING HELPERS
    // ==========================================

    /**
     * Adds an info-level log message via MapApp.
     * @param {string} message - The log message text
     */
    addMessage(message) {
        this.mapApp.addMessage(message);
    }

    /**
     * Adds a warning-level log message via MapApp.
     * @param {string} message - The warning message text
     */
    addWarning(message) {
        this.mapApp.addWarning(message);
    }

    /**
     * Adds an error-level log message via MapApp.
     * @param {string} message - The error message text
     */
    addError(message) {
        this.mapApp.addError(message);
    }


    /**
     * Loads configuration from a JSON object or URL path.
     * @async
     * @param {Object|string} config - Configuration object or URL path to JSON file
     * @returns {Promise<Object>} Validated configuration with defaults applied
     * @throws {Error} If fetch fails or JSON is invalid
     */
    async loadAppConfig(appConfigPath) {
        try {
            // "no-cache" = always revalidate (a 304 when unchanged): this file holds the world list and
            // appCacheBusterVersion, and nothing busts ITS url, while nginx caches /static/ for 30 days
            let appConfigData = await loadJsonDataFromPath(appConfigPath, 'load app config file', DEBUG_SIMULATE_NETWORK_DELAY_MS, {cache: "no-cache"});
            return this.validateAppConfig(appConfigData, appConfigPath);
        } catch (error) {
            const errorMessage = `Failed to load app config file at "${appConfigPath}": ${error}.`;
            this.addError(errorMessage);
            throw new Error(error);
        }
    }

    /**
     * Validates app configuration structure.
     * Ensures required fields exist and adds config path to the data.
     * @param {Object} configData - The app configuration data to validate
     * @param {string} appConfigPath - Path to the configuration file
     * @returns {Object} Validated configuration with configPath added
     * @throws {Error} If required fields are missing
     */
    validateAppConfig(configData, appConfigPath) {
        if (!configData.worlds || !configData.defaultWorldId) {
            const errorMessage = 'App config must specify a worlds list and a defaultWorldId value.'
            this.addError(errorMessage);
            throw new Error(errorMessage);
        }
        // add info
        configData.configPath = appConfigPath;
        return configData;
    }




    /**
     * Loads a world configuration by its ID.
     * Resolves the world config path from appConfig and loads it.
     * @async
     * @param {Object} appConfig - Application configuration with worlds dictionary
     * @param {string} worldId - World ID to load
     * @returns {Promise<Object>} Loaded and validated world configuration
     * @throws {Error} If world ID not found or worldConfigPath is missing
     */
    async loadWorldById(appConfig, worldId) {
        // get the world config url in the appConfigFile
        const worldDict = ConfigLoader.findWorldDictById(appConfig, worldId);
        if (!worldDict) {
            const errorMessage = `World with id "${worldId}" not found in appConfig.`;
            this.addError(errorMessage);
            //throw new Error(errorMessage);
            return false;
        }
        // (checked before resolving it: the resolver throws on a missing path, which hid this message)
        const worldConfigPath = worldDict.worldConfigPath ? fixRelativeChildPathWithParentFilePath(appConfig.configPath, worldDict.worldConfigPath) : '';
        if (!worldConfigPath) {
            const errorMessage = `WorldConfigPath value not found in wold "${worldId}".`;
            this.addError(errorMessage);
            //throw new Error(errorMessage);
            return false;
        }
        return await this.loadWorldConfigFromPath(appConfig, worldConfigPath, worldId);
    }


    /**
     * Gets the world dictionary entry for a given world ID from the app configuration.
     * @param {Object} appConfig - Application configuration with worlds dictionary
     * @param {string} worldId - World ID to look up
     * @returns {Object} The world dictionary entry for the given ID
     * @throws {Error} If world ID not found in appConfig
     */
    getWorldDictById(appConfig, worldId) {
        const worldDict = ConfigLoader.findWorldDictById(appConfig, worldId);
        if (!worldDict) {
            const errorMessage = `World with id "${worldId}" not found in appConfig.`;
            this.addError(errorMessage);
            throw new Error(errorMessage);
        }
        return worldDict;
    }


    /**
     * The app config's entry for a world id, or null if the config has no such world. Only the worlds
     * dictionary's OWN keys count: a world id read from storage, a settings backup or the ?worldId= url
     * parameter may be "constructor" or "__proto__", which a bare worlds[id] resolves to an
     * Object.prototype member (and the startup then failed on every load until storage was cleared).
     * @param {Object} appConfig - Application configuration with worlds dictionary
     * @param {string} worldId - World ID to look up
     * @returns {Object|null} The world dictionary entry, or null
     */
    static findWorldDictById(appConfig, worldId) {
        const worldDict = getOwnValue(appConfig ? appConfig.worlds : null, worldId);
        return (worldDict && typeof worldDict === 'object') ? worldDict : null;
    }


    /**
     * Loads a world configuration from a file path.
     * @async
     * @param {Object} appConfig - Application configuration (used for context)
     * @param {string} worldConfigPath - Path to the world configuration JSON file
     * @param {string} worldId - World ID for the loaded configuration
     * @returns {Promise<Object>} Loaded and validated world configuration
     */
    async loadWorldConfigFromPath(appConfig, worldConfigPath, worldId) {
        try {
            const fetchPath = appendCacheBuster(worldConfigPath, appConfig.appCacheBusterVersion);
            const worldConfigData = await loadJsonDataFromPath(fetchPath, 'load world config file', DEBUG_SIMULATE_NETWORK_DELAY_MS);
            const retv = this.validateWorldConfig(worldConfigData, fetchPath, worldId);
            if (retv) {
                const message = `CasebookMap loaded world configuration from "${fetchPath}".`;
                console.info(message);
                this.addMessage(message);
                return retv;
            }
        }
        catch (error) {
            const errorMessage = `Failed to load world config file from "${worldConfigPath}": ${error}.`;
            this.addError(errorMessage);
            // throw new Error(error);
        }
        return false;
    }


    /**
     * Validates world configuration structure.
     * Ensures required fields exist and adds world ID and config path to the data.
     * @param {Object} configData - The world configuration data to validate
     * @param {string} worldConfigPath - Path to the configuration file
     * @param {string} worldId - World ID to attach to the configuration
     * @returns {Object} Validated configuration with id and configPath added
     * @throws {Error} If required fields are missing
     */
    validateWorldConfig(configData, worldConfigPath, worldId) {
        if (!configData.mapConfig) {
            const errorMessage = 'World config must specify a mapConfig dictionary.'
            this.addError(errorMessage);
            throw new Error(errorMessage);
        }
        // add info
        configData.id = worldId;
        configData.configPath = worldConfigPath;
        // return it
        return configData;
    }



    /**
     * Parses a GDAL2Tiles tilemapresource.xml file to extract tile layer configuration.
     * Extracts tile format, bounds, zoom levels, and constructs the tile URL pattern.
     * Note: GDAL2Tiles always uses TMS format (Y origin at bottom).
     * @async
     * @param {string} xmlUrl - URL to the tilemapresource.xml file
     * @returns {Promise<Object>} Parsed configuration with properties:
     *   - url: Tile URL pattern with {z}/{x}/{y} placeholders
     *   - bounds: [minX, minY, maxX, maxY] extent array
     *   - minZoom: Minimum available zoom level
     *   - maxZoom: Maximum available zoom level
     *   - tileFormat: Always 'tms' for GDAL2Tiles output
     * @throws {Error} If fetch fails or XML is malformed
     */
    async loadAndParseTilemapResourceXml(xmlUrl) {
        try {
            const response = await fetch(xmlUrl);
            if (!response.ok) {
                // just throw error; catch will log and display
                const errorMessage = `${response.statusText}`;
                throw new Error(errorMessage);
            }
            const xmlText = await response.text();

            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(xmlText, 'text/xml');

            // Check for parsing errors
            const parseError = xmlDoc.querySelector('parsererror');
            if (parseError) {
                // just throw error; catch will log and display
                const errorMessage = 'XML error [' + parseError.textContent + ']';
                throw new Error(errorMessage);
            }

            // Extract SRS (empty for raster profile, e.g. "EPSG:3857" for mercator)
            const srs = xmlDoc.querySelector('SRS')?.textContent?.trim() || '';

            // Extract tile format and URL pattern
            const tileFormatElement = xmlDoc.querySelector('TileFormat');
            const extension = tileFormatElement?.getAttribute('extension') || 'png';

            // Extract bounds
            const boundingBox = xmlDoc.querySelector('BoundingBox');
            const minx = parseFloat(boundingBox?.getAttribute('minx') || '-180');
            const miny = parseFloat(boundingBox?.getAttribute('miny') || '-90');
            const maxx = parseFloat(boundingBox?.getAttribute('maxx') || '180');
            const maxy = parseFloat(boundingBox?.getAttribute('maxy') || '90');

            const Origin = xmlDoc.querySelector('Origin');
            const originX = parseFloat(Origin?.getAttribute('x') || '0');
            const originY = parseFloat(Origin?.getAttribute('y') || '0');            

            // Extract zoom levels
            const tileSets = xmlDoc.querySelectorAll('TileSet');
            let minZoom = Infinity;
            let maxZoom = -Infinity;

            tileSets.forEach(tileSet => {
                const order = parseInt(tileSet.getAttribute('order') || '0');
                minZoom = Math.min(minZoom, order);
                maxZoom = Math.max(maxZoom, order);
            });

            // Construct tile URL pattern based on XML location
            // Remove 'tilemapresource.xml' from the end to get base URL
            // Strip query string before calculating base URL for tile path
            const xmlUrlClean = stripQueryString(xmlUrl);
            const baseUrl = xmlUrlClean.replace(/tilemapresource\.xml$/, '');
            const tileUrl = `${baseUrl}{z}/{x}/{y}.${extension}`;

            // GDAL2Tiles ALWAYS generates tiles in TMS format (Y origin at bottom)
            // regardless of the profile used (mercator, geodetic, or raster)
            // The profile only affects the projection, not the tile naming convention
            const tileFormat = 'tms';

            const result = {
                url: tileUrl,
                bounds: [minx, miny, maxx, maxy],
                minZoom: minZoom !== Infinity ? minZoom : 0,
                maxZoom: maxZoom !== -Infinity ? maxZoom : 18,
                tileFormat: tileFormat,
                originX: originX,
                originY: originY,
                srs: srs
            };

            return result;
        } catch (error) {
            const errorMessage = `Failed to parse tilemapresource.xml at "${xmlUrl}": ` + error.message
            this.addError(errorMessage);
            throw new Error(error);
        }
    }

}
